import { z } from 'zod';
import { montarCaminho, requisitar } from './client.js';
import { analisar } from './validacao.js';

/**
 * O painel do dia (RF-070) numa resposta só.
 *
 * Uma tela, uma requisição: quatro chamadas paralelas dariam quatro estados de
 * carregamento e quatro instantes de apuração diferentes numa tela cuja
 * pergunta é "como está AGORA".
 *
 * Todo número daqui tem coluna de origem no banco. O que não tinha — a
 * comparação "vs. ontem" e o selo de contato com o ERP — saiu do contrato E da
 * tela; o motivo de cada um está onde o elemento foi removido
 * (`pages/VisaoGeral.tsx`).
 */

const CAMINHO = montarCaminho('/visao-geral');

/**
 * Campo que a API pode omitir OU mandar nulo, e que chega à tela sempre como
 * `null`: uma coluna que às vezes é `undefined` e às vezes `null` vira dois
 * caminhos de renderização para o mesmo fato — "não há".
 */
function opcional<E extends z.ZodTypeAny>(schema: E) {
  return schema.nullish().transform((valor: z.infer<E> | null | undefined) => valor ?? null);
}

const contagem = z.number().int().nonnegative();

export const operacoesPorTipoSchema = z.object({
  total: contagem,
  coleta: contagem,
  movimentacao: contagem,
  contagemInventario: contagem,
});

/** Os cinco estados da §6.1 que ainda esperam alguém. Backlog, não janela. */
export const filaDeIntegracaoSchema = z.object({
  pendenteDispositivo: contagem,
  registrada: contagem,
  retida: contagem,
  enviando: contagem,
  /** Zero até a Fila B existir (F2.3) — a consulta é real, a tabela é que ainda não recebe. */
  rejeitada: contagem,
});

export const dispositivosDaVisaoGeralSchema = z.object({
  total: contagem,
  ativos: contagem,
  aguardandoPareamento: contagem,
  bloqueados: contagem,
  /** Aparelhos com sessão de operador viva agora. */
  comOperadorConectado: contagem,
  /**
   * AUTO-DECLARADO pelo coletor: a fila mora no SQLite do aparelho e o
   * middleware não a enxerga (RNF-012). Serve para chamar atenção, nunca como
   * contabilidade — por isso o nome não encurta para "pendentes".
   */
  pendenciasDeclaradasNosAparelhos: contagem,
  aparelhosComPendenciaDeclarada: contagem,
});

export const indicadoresSchema = z.object({
  /** Recortadas pela janela, pelo relógio do APARELHO (RF-052). */
  operacoes: operacoesPorTipoSchema,
  coletasConcluidas: contagem,
  vinculosManuais: contagem,
  filaDeIntegracao: filaDeIntegracaoSchema,
  divergenciasEmAberto: contagem,
  inventariosEmConflito: contagem,
  substituicoesPendentes: contagem,
  dispositivos: dispositivosDaVisaoGeralSchema,
});

export const baldeDeHoraSchema = z.object({
  /** Início do balde, em hora UTC cheia. Quem formata o rótulo é o navegador. */
  inicio: z.string(),
  coleta: contagem,
  movimentacao: contagem,
  contagemInventario: contagem,
});

export const contextoDaAtividadeSchema = z.object({
  produto: opcional(z.string()),
  sku: opcional(z.string()),
  /** Nulo quando o registro da trilha não grava filial (coleta que não a resolveu). */
  codFil: opcional(z.number().int()),
  /** O depósito da ação: o contado, o da coleta, ou a origem da movimentação. */
  depositoErp: opcional(z.string()),
  depositoDestino: opcional(z.string()),
  quantidade: opcional(z.string()),
  linha: opcional(z.number().int()),
});

export const linhaDeAtividadeSchema = z.object({
  id: z.string(),
  /** Código do catálogo de auditoria. A TELA decide o texto pelo código. */
  acao: z.string(),
  origem: z.enum(['Coletor', 'Painel', 'Integracao', 'Sistema']),
  /** Matrícula do operador ou login do painel. */
  autor: opcional(z.string()),
  entidade: opcional(z.string()),
  entidadeId: opcional(z.string()),
  /** Relógio do SERVIDOR: a trilha ordena por CHEGADA, não pelo horário do aparelho. */
  registradoEm: z.string(),
  contexto: contextoDaAtividadeSchema,
});

export const tipoDeAtencaoSchema = z.enum([
  'dispositivoSemSincronizar',
  'dispositivoBloqueado',
  'inventarioEmConflito',
  'divergenciaEmAberto',
  'substituicaoPendente',
  'operacaoRetida',
  'operacaoRejeitada',
]);

export const itemDeAtencaoSchema = z.object({
  tipo: tipoDeAtencaoSchema,
  gravidade: z.enum(['erro', 'atencao']),
  /** Quantos fatos este item agrupa (ex.: 3 divergências do mesmo ciclo). */
  quantidade: contagem,
  /** Alvo da navegação — o que ele identifica depende de `tipo`. */
  referenciaId: opcional(z.string()),
  referencia: opcional(z.string()),
  codFil: opcional(z.number().int()),
  depositoErp: opcional(z.string()),
  /** O instante que dá a idade do problema. */
  desde: opcional(z.string()),
});

export const visaoGeralSchema = z.object({
  /** Instante da apuração no SERVIDOR — o "atualizado em" do cabeçalho. */
  geradoEm: z.string(),
  /** Ecoa o recorte que valeu, inclusive o padrão do servidor. */
  janela: z.object({ de: z.string(), ate: z.string() }),
  indicadores: indicadoresSchema,
  /** Uma barra por hora da janela, com os baldes VAZIOS incluídos. */
  operacoesPorHora: z.array(baldeDeHoraSchema),
  /** O teto de leitura do gráfico foi atingido: as barras estão incompletas. */
  operacoesPorHoraTruncado: z.boolean(),
  atividadeRecente: z.array(linhaDeAtividadeSchema),
  requerAtencao: z.array(itemDeAtencaoSchema),
  /**
   * O teto de leitura da lista foi atingido: há mais itens esperando gente do
   * que os listados. Não existe TOTAL no contrato — as sete consultas do
   * middleware têm teto próprio e duas agrupam, então o número exato custaria
   * sete contagens a mais na tela mais recarregada do painel. A marca é o que
   * se apura de graça, e é o que impede a tela de exibir o teto como total.
   */
  requerAtencaoTruncado: z.boolean(),
});

export type OperacoesPorTipo = z.infer<typeof operacoesPorTipoSchema>;
export type Indicadores = z.infer<typeof indicadoresSchema>;
export type BaldeDeHora = z.infer<typeof baldeDeHoraSchema>;
export type LinhaDeAtividade = z.infer<typeof linhaDeAtividadeSchema>;
export type TipoDeAtencao = z.infer<typeof tipoDeAtencaoSchema>;
export type ItemDeAtencao = z.infer<typeof itemDeAtencaoSchema>;
export type VisaoGeralDoDia = z.infer<typeof visaoGeralSchema>;

/**
 * Recorte da apuração, em ISO.
 *
 * Quem manda o fuso é o NAVEGADOR: o servidor não conhece o fuso do armazém e
 * não vai adivinhar. Nada informado deixa o padrão do servidor valer (24h até
 * agora).
 */
export type RecorteDaVisaoGeral = {
  readonly de?: string;
  readonly ate?: string;
};

export async function consultarVisaoGeral(
  recorte: RecorteDaVisaoGeral = {},
): Promise<VisaoGeralDoDia> {
  const corpo = await requisitar(CAMINHO, { consulta: { de: recorte.de, ate: recorte.ate } });

  return analisar(visaoGeralSchema, corpo, CAMINHO);
}
